/**
 * Admin Commission Overrides — R2 admin UI
 * =========================================
 * Founder/admin tooling to grant, edit, disable, and inspect creator commission
 * overrides via the existing R1 API contracts. NO backend changes here.
 *
 * Endpoints used (all admin-gated, returns 403 for non-admin):
 *   GET    /api/admin/commission-overrides?creator_user_id&active&limit&offset
 *   POST   /api/admin/commission-overrides
 *   PATCH  /api/admin/commission-overrides/{id}
 *   GET    /api/admin/creator-commission/{creator_id}
 *   GET    /api/admin/creators?limit&offset                       (existing R1 admin list)
 *
 * Safety UX:
 *   - All apply actions go through a two-step confirm.
 *   - Below-floor values are clamped on the backend AND warned in the UI.
 *   - Below 5% is explicitly blocked in the UI; 5% itself requires confirm.
 *   - Disable shows "audit row remains" reassurance.
 *
 * Forbidden: NO Stripe / payment / payout / subscription / Connect /
 * commission-helper / referral / reward mutation. This component ONLY drives
 * the existing override endpoints; commission math runs server-side.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { AlertTriangle, CheckCircle, Loader2, Search, ShieldCheck, X, Info, Plus, Power, Eye, EyeOff } from 'lucide-react';

import { API_URL } from './config';

const OVERRIDE_REASONS = [
  'First 100 Creator',
  'Early Adopter',
  'Founder Approved',
  'Strategic Partner',
  'Custom',
];

const MIN_FLOOR = 5; // mirrors server-side MIN_COMMISSION_RATE

const fmt = (n) => (n == null ? '—' : `${n}%`);
const fmtDate = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString();
  } catch (e) { return String(v); }
};

const apiHeaders = (token) => ({
  'Content-Type': 'application/json',
  Authorization: token ? `Bearer ${token}` : '',
});

// ──────────────────────────────────────────────────────────────────────────
// Root component — mounted as a new tab in AdminDashboard
// ──────────────────────────────────────────────────────────────────────────
export const AdminCommissionOverrides = ({ token }) => {
  const [overrides, setOverrides] = useState({ active: [], inactive: [] });
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  const [showInactive, setShowInactive] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  const loadOverrides = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const [activeRes, inactiveRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/commission-overrides?active=true&limit=200`, { headers: apiHeaders(token) }),
        fetch(`${API_URL}/api/admin/commission-overrides?active=false&limit=200`, { headers: apiHeaders(token) }),
      ]);
      if (!activeRes.ok) throw new Error(`Active list HTTP ${activeRes.status}`);
      if (!inactiveRes.ok) throw new Error(`Inactive list HTTP ${inactiveRes.status}`);
      const activeData = await activeRes.json();
      const inactiveData = await inactiveRes.json();
      setOverrides({ active: activeData.data || [], inactive: inactiveData.data || [] });
    } catch (e) {
      setListError(e.message || 'Could not load overrides');
    } finally {
      setListLoading(false);
    }
  }, [token]);

  useEffect(() => { loadOverrides(); }, [loadOverrides]);

  const refreshBreakdown = useCallback(async (creatorId) => {
    if (!creatorId) return;
    setBreakdownLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/creator-commission/${creatorId}`, { headers: apiHeaders(token) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setBreakdown(await r.json());
    } catch (e) {
      setBreakdown(null);
    } finally {
      setBreakdownLoading(false);
    }
  }, [token]);

  const onCreatorPicked = useCallback((creator) => {
    setSelectedCreator(creator);
    setShowCreateForm(false);
    refreshBreakdown(creator.id);
  }, [refreshBreakdown]);

  const onOverrideMutated = useCallback(() => {
    loadOverrides();
    if (selectedCreator) refreshBreakdown(selectedCreator.id);
  }, [loadOverrides, refreshBreakdown, selectedCreator]);

  return (
    <div data-testid="admin-commission-overrides-tab" className="space-y-5">
      {/* Header / Rules card */}
      <div className="bg-[#1a1d27] rounded-xl border border-[#2a2d3a] p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-white">Founder Commission Overrides</h2>
            <p className="text-xs text-gray-400 mt-1 max-w-3xl">
              Standard 20% / new-creator promo 15% (90 days) / referral reward −5pp (capped once).
              Founder/admin override can take a creator&apos;s platform commission as low as <strong className="text-emerald-400">5%</strong>.
              Below 5% is clamped server-side and reported as <span className="text-amber-400">floor_clamped</span>.
              Old payments are immutable.
            </p>
          </div>
        </div>
      </div>

      {/* Creator selector + breakdown */}
      <CreatorPicker token={token} onPick={onCreatorPicked} selected={selectedCreator} />

      {selectedCreator && (
        <CreatorBreakdown
          breakdown={breakdown}
          loading={breakdownLoading}
          creator={selectedCreator}
          onGrantClick={() => setShowCreateForm(true)}
        />
      )}

      {/* Create form (controlled by selectedCreator + showCreateForm) */}
      {showCreateForm && selectedCreator && (
        <CreateOverrideForm
          token={token}
          creator={selectedCreator}
          existingActive={breakdown?.breakdown?.override}
          onClose={() => setShowCreateForm(false)}
          onSuccess={onOverrideMutated}
        />
      )}

      {/* Active overrides list */}
      <OverridesList
        token={token}
        title={`Active overrides (${overrides.active.length})`}
        items={overrides.active}
        loading={listLoading}
        error={listError}
        showDisable
        onDisabled={onOverrideMutated}
        testIdPrefix="active-override"
      />

      {/* Inactive / history */}
      <div className="bg-[#1a1d27] rounded-xl border border-[#2a2d3a] p-5">
        <button
          onClick={() => setShowInactive((v) => !v)}
          className="flex items-center gap-2 text-xs font-semibold text-gray-300 hover:text-white transition"
          data-testid="toggle-inactive-overrides-btn"
        >
          {showInactive ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {showInactive ? 'Hide' : 'Show'} historical / inactive ({overrides.inactive.length})
        </button>
        {showInactive && (
          <div className="mt-4">
            <OverridesList
              token={token}
              title=""
              items={overrides.inactive}
              loading={listLoading}
              error={null}
              showDisable={false}
              onDisabled={onOverrideMutated}
              testIdPrefix="inactive-override"
              compact
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// CreatorPicker — search/select from /api/admin/creators
// ──────────────────────────────────────────────────────────────────────────
const CreatorPicker = ({ token, onPick, selected }) => {
  const [query, setQuery] = useState('');
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/creators?limit=200&include_internal=true`, { headers: apiHeaders(token) });
      const d = await r.json();
      setCreators(Array.isArray(d.data) ? d.data : (d.creators || d || []));
    } catch (e) {
      setCreators([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!query.trim()) return creators.slice(0, 25);
    const q = query.toLowerCase();
    return creators
      .filter((c) => (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q) || (c.id || '').toLowerCase().includes(q))
      .slice(0, 25);
  }, [creators, query]);

  return (
    <div className="bg-[#1a1d27] rounded-xl border border-[#2a2d3a] p-5">
      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-gray-400" /> Find creator
      </h3>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by name, email, or ID"
        data-testid="commission-creator-search"
        className="w-full bg-[#0f1117] border border-[#2a2d3a] text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:border-blue-500"
      />
      {loading && <div className="mt-3 text-xs text-gray-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading creators…</div>}
      {!loading && filtered.length > 0 && (
        <div className="mt-3 max-h-72 overflow-y-auto divide-y divide-[#2a2d3a]" data-testid="commission-creator-results">
          {filtered.map((c) => {
            const isSelected = selected?.id === c.id;
            return (
              <button
                key={c.id}
                onClick={() => onPick(c)}
                data-testid={`commission-creator-row-${c.id}`}
                className={`w-full flex items-center justify-between gap-3 py-2 px-2 hover:bg-[#262a38] text-left rounded ${isSelected ? 'bg-[#1f2330]' : ''}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{c.name || '(unnamed)'}</div>
                  <div className="text-[10px] text-gray-500 truncate">{c.email || c.id}</div>
                </div>
                <div className="text-xs text-gray-300 font-mono shrink-0">{fmt(c.commission_rate)}</div>
              </button>
            );
          })}
        </div>
      )}
      {!loading && filtered.length === 0 && query.trim() && (
        <div className="mt-3 text-xs text-gray-500" data-testid="commission-creator-empty">No matching creators.</div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// CreatorBreakdown — why does this creator have their current rate?
// ──────────────────────────────────────────────────────────────────────────
const CreatorBreakdown = ({ breakdown, loading, creator, onGrantClick }) => {
  if (loading) return (
    <div className="bg-[#1a1d27] rounded-xl border border-[#2a2d3a] p-5 text-xs text-gray-500 flex items-center gap-2">
      <Loader2 className="w-3 h-3 animate-spin" /> Loading breakdown…
    </div>
  );
  if (!breakdown) return null;
  const b = breakdown.breakdown || {};
  return (
    <div className="bg-[#1a1d27] rounded-xl border border-[#2a2d3a] p-5" data-testid="commission-breakdown">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-white">{creator.name || '(unnamed)'}</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">{creator.email}</p>
          <p className="text-[10px] text-gray-600 font-mono mt-0.5">{creator.id}</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Current effective rate</div>
          <div className="text-3xl font-bold text-emerald-400" data-testid="commission-current-rate">{fmt(breakdown.current_rate)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <BreakdownStat label="Base rate" value={fmt(b.base_rate)} testId="bd-base" />
        <BreakdownStat label="Early-adopter promo" value={b.early_adopter_promo_active ? 'Active' : 'Off'} accent={b.early_adopter_promo_active ? 'emerald' : 'gray'} testId="bd-promo" />
        <BreakdownStat label="Referral reward" value={b.referral_reward_active ? `−${b.referral_reward_pp || 5}pp` : 'Off'} accent={b.referral_reward_active ? 'emerald' : 'gray'} testId="bd-referral" />
        <BreakdownStat label="Override" value={b.override_active ? 'Active' : 'Off'} accent={b.override_active ? 'amber' : 'gray'} testId="bd-override" />
      </div>

      {b.override_active && b.override && (
        <div className="rounded-md bg-amber-900/20 border border-amber-800 p-3 text-xs text-amber-200 mb-3" data-testid="commission-active-override-info">
          <p className="font-semibold">Active override: {b.override.override_type === 'fixed_platform_rate' ? `Fixed ${b.override.value}%` : `−${b.override.value}pp`}</p>
          <p className="opacity-90 mt-0.5">Reason: {b.override.reason}</p>
          <p className="opacity-70 mt-0.5">Granted by {b.override.granted_by_admin_email || b.override.granted_by_admin_id} · {fmtDate(b.override.created_at)}</p>
        </div>
      )}

      <button
        onClick={onGrantClick}
        className="px-3 py-1.5 text-xs font-medium bg-emerald-700 hover:bg-emerald-600 text-white rounded-md transition flex items-center gap-1.5"
        data-testid="grant-override-btn"
      >
        <Plus className="w-3.5 h-3.5" />
        {b.override_active ? 'Replace override' : 'Grant override'}
      </button>
      <p className="text-[10px] text-gray-500 mt-2">
        Floor: {b.floor}% — overrides below this are clamped server-side.
        {b.override_active && ' Granting a new override deactivates the existing one (audit row preserved).'}
      </p>
    </div>
  );
};

const BreakdownStat = ({ label, value, accent = 'gray', testId }) => {
  const cmap = {
    emerald: 'bg-emerald-900/20 border-emerald-800 text-emerald-200',
    amber: 'bg-amber-900/20 border-amber-800 text-amber-200',
    gray: 'bg-[#1f2330] border-[#2a2d3a] text-gray-300',
  };
  return (
    <div className={`rounded border px-2 py-1.5 ${cmap[accent] || cmap.gray}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// CreateOverrideForm — two-step confirm, floor warning, below-5% blocked
// ──────────────────────────────────────────────────────────────────────────
const CreateOverrideForm = ({ token, creator, existingActive, onClose, onSuccess }) => {
  const [overrideType, setOverrideType] = useState('fixed_platform_rate');
  const [value, setValue] = useState(15);
  const [reason, setReason] = useState(OVERRIDE_REASONS[0]);
  const [customReason, setCustomReason] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [notes, setNotes] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const effectiveReason = reason === 'Custom' ? customReason.trim() : reason;
  const numericValue = Number.parseInt(value, 10);
  const isBelowFloor = overrideType === 'fixed_platform_rate' && Number.isFinite(numericValue) && numericValue < MIN_FLOOR;
  const isAtFloor = overrideType === 'fixed_platform_rate' && numericValue === MIN_FLOOR;
  const isZeroOrNeg = Number.isFinite(numericValue) && numericValue <= 0;

  const canConfirm = (
    Number.isFinite(numericValue) &&
    !isBelowFloor &&
    !isZeroOrNeg &&
    effectiveReason.length > 0 &&
    (overrideType === 'fixed_platform_rate' ? numericValue <= 100 : numericValue >= 0)
  );

  const summary = useMemo(() => {
    if (overrideType === 'fixed_platform_rate') {
      return `This will set ${creator.name || creator.email}'s platform commission to ${numericValue}%.`;
    }
    return `This will apply a ${numericValue}pp discount from the base rate for ${creator.name || creator.email}.`;
  }, [creator, overrideType, numericValue]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/commission-overrides`, {
        method: 'POST',
        headers: apiHeaders(token),
        body: JSON.stringify({
          creator_user_id: creator.id,
          override_type: overrideType,
          value: numericValue,
          reason: effectiveReason,
          ends_at: endsAt || null,
          notes: notes || null,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setResult(data);
      onSuccess();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#1a1d27] rounded-xl border border-emerald-800 p-5" data-testid="create-override-form">
      <div className="flex items-start justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-white">
          {existingActive ? 'Replace active override' : 'Grant new override'} — {creator.name || creator.email}
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-create-form-btn"><X className="w-4 h-4" /></button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] text-gray-400 uppercase tracking-wide">Override type</label>
          <select
            value={overrideType}
            onChange={(e) => setOverrideType(e.target.value)}
            data-testid="override-type-select"
            className="mt-1 w-full bg-[#0f1117] border border-[#2a2d3a] text-white text-sm rounded px-2 py-1.5"
          >
            <option value="fixed_platform_rate">Fixed platform rate (%)</option>
            <option value="percentage_point_discount">Percentage-point discount (−pp)</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] text-gray-400 uppercase tracking-wide">Value</label>
          <input
            type="number"
            min={overrideType === 'fixed_platform_rate' ? 0 : 0}
            max={overrideType === 'fixed_platform_rate' ? 100 : 100}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="override-value-input"
            className="mt-1 w-full bg-[#0f1117] border border-[#2a2d3a] text-white text-sm rounded px-2 py-1.5"
          />
          <div className="text-[10px] mt-1">
            {overrideType === 'fixed_platform_rate' ? (
              <>Final platform rate (creator keeps {Number.isFinite(numericValue) ? 100 - numericValue : '?'}% of revenue).</>
            ) : (
              <>Subtracted from the base rate (20 standard / 15 promo).</>
            )}
          </div>
        </div>
        <div>
          <label className="text-[11px] text-gray-400 uppercase tracking-wide">Reason / label</label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="override-reason-select"
            className="mt-1 w-full bg-[#0f1117] border border-[#2a2d3a] text-white text-sm rounded px-2 py-1.5"
          >
            {OVERRIDE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {reason === 'Custom' && (
            <input
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Custom reason"
              data-testid="override-custom-reason-input"
              className="mt-2 w-full bg-[#0f1117] border border-[#2a2d3a] text-white text-sm rounded px-2 py-1.5"
            />
          )}
        </div>
        <div>
          <label className="text-[11px] text-gray-400 uppercase tracking-wide">Ends at (optional)</label>
          <input
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            data-testid="override-ends-at-input"
            className="mt-1 w-full bg-[#0f1117] border border-[#2a2d3a] text-white text-sm rounded px-2 py-1.5"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[11px] text-gray-400 uppercase tracking-wide">Admin notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            data-testid="override-notes-input"
            className="mt-1 w-full bg-[#0f1117] border border-[#2a2d3a] text-white text-sm rounded px-2 py-1.5"
          />
        </div>
      </div>

      {/* Warnings */}
      {isBelowFloor && (
        <div className="mt-3 rounded border border-red-800 bg-red-900/30 px-3 py-2 text-xs text-red-300 flex items-start gap-2" data-testid="below-floor-warning">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Value is below the {MIN_FLOOR}% platform commission floor. Submission blocked.
        </div>
      )}
      {isZeroOrNeg && !isBelowFloor && (
        <div className="mt-3 rounded border border-red-800 bg-red-900/30 px-3 py-2 text-xs text-red-300 flex items-start gap-2" data-testid="zero-rate-warning">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          A zero or negative platform commission is not allowed.
        </div>
      )}
      {isAtFloor && (
        <div className="mt-3 rounded border border-amber-800 bg-amber-900/30 px-3 py-2 text-xs text-amber-200 flex items-start gap-2" data-testid="at-floor-warning">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Value equals the {MIN_FLOOR}% floor — this is the lowest permitted platform rate. Please double-check before confirming.
        </div>
      )}

      {/* Two-step confirm */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!confirming && (
          <button
            onClick={() => setConfirming(true)}
            disabled={!canConfirm || submitting}
            data-testid="override-review-btn"
            className="px-3 py-1.5 text-xs font-medium bg-emerald-700 hover:bg-emerald-600 text-white rounded-md transition disabled:opacity-50"
          >
            Review & confirm
          </button>
        )}
        {confirming && !result && (
          <div className="w-full rounded border border-emerald-800 bg-emerald-900/20 p-3 space-y-2">
            <p className="text-xs text-emerald-200" data-testid="override-confirm-summary">{summary}</p>
            {existingActive && (
              <p className="text-[11px] text-amber-300">The current active override ({existingActive.override_type === 'fixed_platform_rate' ? `${existingActive.value}%` : `−${existingActive.value}pp`}) will be deactivated. Audit row preserved.</p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="override-confirm-apply-btn"
                className="px-3 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded-md transition disabled:opacity-50"
              >
                {submitting ? 'Applying…' : 'Confirm apply'}
              </button>
              <button
                onClick={() => setConfirming(false)}
                disabled={submitting}
                data-testid="override-confirm-cancel-btn"
                className="px-3 py-1.5 text-xs font-medium bg-[#1f2330] border border-[#2a2d3a] text-gray-300 hover:bg-[#262a38] rounded-md transition disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        <button onClick={onClose} disabled={submitting} className="px-3 py-1.5 text-xs font-medium bg-transparent text-gray-400 hover:text-white transition">Close</button>
      </div>

      {error && (
        <div className="mt-3 rounded border border-red-800 bg-red-900/30 px-3 py-2 text-xs text-red-300" data-testid="override-error">{error}</div>
      )}
      {result && (
        <div className="mt-3 rounded border border-emerald-800 bg-emerald-900/30 px-3 py-2 text-xs text-emerald-200" data-testid="override-success">
          <p className="font-semibold flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5" /> Override applied.</p>
          {result.floor_clamped && (
            <p className="mt-1 text-amber-300">Server clamped value to floor (5%). The override now sits at 5%.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// OverridesList — active + inactive views
// ──────────────────────────────────────────────────────────────────────────
const OverridesList = ({ token, title, items, loading, error, showDisable, onDisabled, testIdPrefix, compact = false }) => {
  return (
    <div className={compact ? '' : 'bg-[#1a1d27] rounded-xl border border-[#2a2d3a] p-5'}>
      {title && <h3 className="text-sm font-semibold text-white mb-3">{title}</h3>}
      {loading && (
        <div className="text-xs text-gray-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
      )}
      {error && (
        <div className="text-xs text-red-400">{error}</div>
      )}
      {!loading && !error && items.length === 0 && (
        <div className="text-xs text-gray-500" data-testid={`${testIdPrefix}-empty`}>None.</div>
      )}
      {!loading && items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500 border-b border-[#2a2d3a]">
                <th className="py-2 pr-3">Creator</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3">Value</th>
                <th className="py-2 pr-3">Reason</th>
                <th className="py-2 pr-3">Granted by</th>
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3">Ends</th>
                <th className="py-2 pr-3">Floor</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <OverrideRow
                  key={o.id}
                  o={o}
                  showDisable={showDisable}
                  token={token}
                  onDisabled={onDisabled}
                  testIdPrefix={testIdPrefix}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const OverrideRow = ({ o, showDisable, token, onDisabled, testIdPrefix }) => {
  const [disabling, setDisabling] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [err, setErr] = useState(null);

  const disable = async () => {
    setDisabling(true);
    setErr(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/commission-overrides/${o.id}`, {
        method: 'PATCH',
        headers: apiHeaders(token),
        body: JSON.stringify({ active: false, deactivated_reason: 'admin_ui_disable' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
      onDisabled();
    } catch (e) {
      setErr(e.message);
    } finally {
      setDisabling(false);
      setConfirmDisable(false);
    }
  };

  return (
    <tr className="border-b border-[#2a2d3a] hover:bg-[#1f2330]" data-testid={`${testIdPrefix}-row-${o.id}`}>
      <td className="py-2 pr-3 font-mono text-[10px] text-gray-300">{o.creator_user_id}</td>
      <td className="py-2 pr-3">{o.override_type === 'fixed_platform_rate' ? 'Fixed' : '−pp'}</td>
      <td className="py-2 pr-3">{o.override_type === 'fixed_platform_rate' ? `${o.value}%` : `${o.value}pp`}</td>
      <td className="py-2 pr-3">{o.reason}</td>
      <td className="py-2 pr-3 text-gray-400">{o.granted_by_admin_email || o.granted_by_admin_id?.slice(0, 8)}</td>
      <td className="py-2 pr-3 text-gray-400">{fmtDate(o.created_at)}</td>
      <td className="py-2 pr-3 text-gray-400">{o.ends_at ? fmtDate(o.ends_at) : '—'}</td>
      <td className="py-2 pr-3">
        {o.floor_clamped ? <span className="text-amber-400" data-testid={`${testIdPrefix}-floor-clamped-${o.id}`}>clamped</span> : <span className="text-gray-500">—</span>}
      </td>
      <td className="py-2 pr-3">
        {showDisable && o.active && !confirmDisable && (
          <button
            onClick={() => setConfirmDisable(true)}
            data-testid={`disable-override-btn-${o.id}`}
            className="px-2 py-1 text-[11px] bg-red-900/40 border border-red-800 text-red-200 hover:bg-red-800/40 rounded transition flex items-center gap-1"
          >
            <Power className="w-3 h-3" /> Disable
          </button>
        )}
        {showDisable && o.active && confirmDisable && (
          <div className="flex items-center gap-1">
            <button
              onClick={disable}
              disabled={disabling}
              data-testid={`disable-override-confirm-btn-${o.id}`}
              className="px-2 py-1 text-[11px] bg-red-700 hover:bg-red-600 text-white rounded disabled:opacity-50"
            >
              {disabling ? '…' : 'Confirm'}
            </button>
            <button
              onClick={() => setConfirmDisable(false)}
              disabled={disabling}
              className="px-2 py-1 text-[11px] bg-[#1f2330] border border-[#2a2d3a] text-gray-300 hover:bg-[#262a38] rounded"
            >
              Cancel
            </button>
          </div>
        )}
        {err && <p className="text-[10px] text-red-400 mt-1">{err}</p>}
      </td>
    </tr>
  );
};

export default AdminCommissionOverrides;
